import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Flame, Lightbulb, ShieldAlert } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { StatusBadge } from "@/shared/ui/StatusBadge";
import { Pagination } from "@/shared/ui/pagination";
import { useClampedPage } from "@/shared/hooks/useClampedPage";
import { UNMATCHED_STATUS_LABELS } from "./constants";
import { aiKefuService, type CsUnmatchedQuestion, type RiskAlert } from "../../api/aiKefuService";
import { buildSuggestions } from "./helpers";
import { UnmatchedCard } from "./UnmatchedCard";

const PAGE_SIZE = 20;

type Props = {
  /** 全量未命中问题(所有状态,lastSeenAt 降序)。open 清单在此客户端分页;hot/建议跨全量算。 */
  questions: CsUnmatchedQuestion[];
  onUpdateStatus: (id: number, status: string) => void;
  onExport: () => void;
};

export function OptimizeTab({ questions, onUpdateStatus, onExport }: Props) {
  const { t } = useTranslation("customer-service");
  // 热点 = 高频未命中 TOP 5(含已处理):跨全量按 hitCount 排。建议 = 全量 open 按 hitCount top5(helper 内部滤 open)。
  const suggestions = buildSuggestions(questions, t);
  const hotQuestions = [...questions].sort((a, b) => b.hitCount - a.hitCount).slice(0, 5);
  // 清单只展示 open(已处理移出清单),客户端分页(0 基)。标记已解决/忽略使 open 变少越界时 clamp 回末页。
  const openQuestions = useMemo(() => questions.filter((q) => q.status === "open"), [questions]);
  const [page, setPage] = useState(0);
  const maxPage = Math.max(0, Math.ceil(openQuestions.length / PAGE_SIZE) - 1);
  useClampedPage({ page, maxPage, setPage });
  const pageRows = openQuestions.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const { data: alerts = [] } = useQuery<RiskAlert[]>({
    queryKey: ["cs-risk-alerts"],
    queryFn: aiKefuService.listRiskAlerts,
    // 运营指标分钟级别变化；别每次切 tab 都发请求
    staleTime: 60 * 1000,
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
      <div className="lg:col-span-2 space-y-4">
        <UnmatchedCard
          questions={pageRows}
          totalOpen={openQuestions.length}
          onUpdateStatus={onUpdateStatus}
          onExport={onExport}
        />
        {openQuestions.length > PAGE_SIZE && (
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={openQuestions.length}
            showTotal
            onPageChange={setPage}
          />
        )}

        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="section-title flex items-center gap-2">
              <Flame className="h-4 w-4 text-destructive" />
              {t("optimize.hotTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {hotQuestions.map((q, i) => (
              <div
                key={q.id}
                className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40 fx-enter fx-hover-lift"
                style={{ animationDelay: `${i * 30}ms` }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-muted-foreground w-5">#{i + 1}</span>
                  <span className="text-sm">{q.question}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-muted-foreground">
                    {t("optimize.hotCount", { count: q.hitCount })}
                  </span>
                  <StatusBadge
                    type={
                      q.status === "open" ? "error" : q.status === "resolved" ? "success" : "info"
                    }
                  >
                    {UNMATCHED_STATUS_LABELS[q.status] ?? "未知状态"}
                  </StatusBadge>
                </div>
              </div>
            ))}
            {questions.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-4">
                {t("optimize.hotEmpty")}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="section-title flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-warning" />
              {t("optimize.suggestTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {suggestions.map((s, i) => (
              <div
                key={i}
                className="p-3 rounded-lg border fx-enter fx-hover-lift"
                style={{ animationDelay: `${i * 30}ms` }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{s.topic}</span>
                  <StatusBadge
                    type={
                      s.urgency === t("optimize.urgencyHigh")
                        ? "error"
                        : s.urgency === t("optimize.urgencyMedium")
                          ? "warning"
                          : "info"
                    }
                  >
                    {s.urgency}
                  </StatusBadge>
                </div>
                <p className="text-2xs text-muted-foreground mt-1">{s.reason}</p>
                <div className="mt-2">
                  <span className="text-2xs text-muted-foreground">
                    {t("optimize.suggestSource", { source: s.source })}
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="shadow-sm border-destructive/20">
          <CardHeader className="pb-2">
            <CardTitle className="section-title flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-destructive" />
              {t("optimize.riskTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-3">
                {t("optimize.riskEmpty")}
              </p>
            ) : (
              alerts.map((a, i) => (
                <div
                  key={`${a.category}-${i}`}
                  className="p-3 rounded-lg bg-muted/30 fx-enter"
                  style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
                >
                  <div className="flex items-start gap-2">
                    <div
                      className={`h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 ${a.level === "error" ? "bg-destructive fx-glow" : "bg-warning"}`}
                    />
                    <p className="text-xs leading-relaxed">{a.message}</p>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
